/**
 * Global enrollment data refresh utility
 * Triggers refresh across all components after rollup/rollback operations
 */

import { useEffect } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { EnhancedErrorLogger } from '@/utils/error-tracking/enhanced-logger';

export type EnrollmentOperation = 'rollup' | 'rollback' | 'manual-rollup';

export interface EnrollmentChangeEvent {
  operation: EnrollmentOperation;
  academicYear: string;
  affectedStudents?: number;
  timestamp: number;
}

/**
 * Triggers a global refresh of enrollment data across all components
 */
export function triggerEnrollmentRefresh(details: Omit<EnrollmentChangeEvent, 'timestamp'>) {
  const event = new CustomEvent('enrollment-data-changed', {
    detail: {
      ...details,
      timestamp: Date.now()
    }
  });

  // Show notification
  const message = details.operation === 'rollup' 
    ? `Rollup completed. Refreshing enrollment data...`
    : details.operation === 'rollback'
    ? `Rollback completed. Restoring enrollment data...`
    : `Manual rollup completed. Updating data...`;
    
  toast.info(message);

  // Dispatch event
  window.dispatchEvent(event);

}

/**
 * Hook to listen for enrollment data changes
 */
export function useEnrollmentRefresh(
  onRefresh: () => void | Promise<void>,
  deps: React.DependencyList = []
) {
  useEffect(() => {
    const handleEnrollmentChange = async (event: Event) => {
      const customEvent = event as CustomEvent<EnrollmentChangeEvent>;
      
      try {
        await onRefresh();
      } catch (error) {
        toast.error('Failed to refresh data');
      }
    };

    window.addEventListener('enrollment-data-changed', handleEnrollmentChange);
    return () => window.removeEventListener('enrollment-data-changed', handleEnrollmentChange);
  }, [...deps, onRefresh]);
}

/**
 * Invalidate React Query caches for enrollment data.
 *
 * Enrollment operations (rollup / rollback / manual-rollup) mutate many tables at once —
 * applications, school capacity, analytics, transportation and various dashboards. Query
 * keys across this codebase are NOT consistently namespaced or year-scoped (e.g.
 * 'applications', 'applications-paginated', 'applications-paged', 'application-stats',
 * 'school-capacity'), so a hardcoded partial key list silently misses caches and leaves
 * stale, post-rollup data on screen. These operations are infrequent and heavy, so a full
 * invalidation — guaranteeing every active view refetches — is the correct trade-off.
 *
 * NOTE: the previous implementation used the React Query v4 array form
 * (`invalidateQueries([key])`), which is a no-op under the v5 API installed here; v5
 * expects a filters object. That bug meant enrollment caches were never invalidated.
 */
export function invalidateEnrollmentQueries(queryClient: QueryClient) {
  return queryClient.invalidateQueries();
}

/**
 * Priority refresh queue to avoid overwhelming the server
 */
class RefreshQueue {
  private queue: Array<() => Promise<void>> = [];
  private processing = false;

  async add(refreshFn: () => Promise<void>, priority: 'high' | 'medium' | 'low' = 'medium') {
    // Add to queue based on priority
    if (priority === 'high') {
      this.queue.unshift(refreshFn);
    } else {
      this.queue.push(refreshFn);
    }

    if (!this.processing) {
      this.process();
    }
  }

  private async process() {
    this.processing = true;

    while (this.queue.length > 0) {
      const fn = this.queue.shift();
      if (fn) {
        try {
          await fn();
          // Small delay between refreshes to avoid overwhelming server
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (error) {
          console.error('Batch refresh failed:', error);
          EnhancedErrorLogger.logError(error instanceof Error ? error : new Error(String(error)), {
            category: 'EnrollmentRefresh',
            context: 'batch refresh',
            severity: 'medium'
          });
        }
      }
    }

    this.processing = false;
  }
}

export const refreshQueue = new RefreshQueue();